import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Col,
  Card,
  CardBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Row,
  Button,
} from "reactstrap";
import { DebounceInput } from "react-debounce-input";
import { PaginateQuery, SupplicationDto } from "../../interfaces";
import { Paginator, TableSpinner } from "../../components";
import { NoResultFound } from "../../components/Common";
import { AddEditSupplicationModal } from "./AddEditSupplicationModal";
import DeleteModal from "../../components/Common/DeleteModal";
import Masonry from "react-masonry-component";
import {
  useDeleteSupplicationMutation,
  useLazyGetSupplicationsQuery,
} from "../../store/api/supplicationApi";

export const SupplicationsPage = () => {
  //#region hooks
  const { t, i18n } = useTranslation();
  //#endregion

  document.title = `${t("documentTitle", {
    pageName: "controlAppSupplications",
  })}`;

  //#region internal state
  const initState = new PaginateQuery(12);
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  //#endregion

  //#region api
  const [
    getSupplications,
    {
      isLoading: isLoadingSupplications,
      isSuccess: isSuccessSupplications,
      data: dataSupplications,
    },
  ] = useLazyGetSupplicationsQuery();

  const [
    deleteSupplication,
    {
      isLoading: isLoadingDeleteSupplication,
      isSuccess: isSuccessDeleteSupplication,
      data: dataDeleteSupplication,
    },
  ] = useDeleteSupplicationMutation();
  //#endregion

  //#region effects
  useEffect(() => {
    getSupplications(paginateQuery);
  }, [paginateQuery]);

  useEffect(() => {
    if (!isLoadingDeleteSupplication && isSuccessDeleteSupplication) {
      setDeletingItemId(null);
      setShowDeleteModal(false);
      getSupplications(paginateQuery);
    }
  }, [isLoadingDeleteSupplication]);

  //#endregion

  //#region handlers
  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = () => {
    deleteSupplication(deletingItemId as string);
    setDeletingItemId(null);
  };

  //#endregion

  return (
    <React.Fragment>
      <Row className="mb-4">
        <Col sm={4} className="mb-2">
          {dataDeleteSupplication &&
            dataDeleteSupplication.data &&
            dataDeleteSupplication.data.length > 0 && (
              <div className="search-box">
                <DebounceInput
                  className="form-control"
                  placeholder={`${t("searchPlaceholder")}...`}
                  minLength={2}
                  debounceTimeout={300}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onSearch(event.target.value)
                  }
                />
                <i className="ri-search-line search-icon"></i>
              </div>
            )}
        </Col>
        <Col className="col-sm-auto ms-auto">
          <div className="list-grid-nav hstack gap-1">
            <Button color="success" onClick={onAdd}>
              <i className="ri-add-fill me-1 align-bottom"></i>
              {t("addItem", { item: "supplication" })}
            </Button>
          </div>
        </Col>
      </Row>
      {isLoadingSupplications && <TableSpinner />}
      {!isLoadingSupplications &&
        isSuccessSupplications &&
        (!dataSupplications?.data || dataSupplications.data?.length === 0) && (
          <NoResultFound />
        )}

      <Masonry className="row gallery-wrapper">
        {!isLoadingSupplications &&
          isSuccessSupplications &&
          dataSupplications?.data &&
          dataSupplications.data.length > 0 &&
          dataSupplications?.data.map(
            (item: SupplicationDto, index: number) => (
              <Col
                xxl={3}
                xl={4}
                sm={6}
                className="element-item project designing development"
                key={index}
              >
                <Card>
                  <CardBody>
                    <div
                      className={`p-3 mt-n3 mx-n3 bg-soft-subtle rounded-top`}
                    >
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <h5 className="mb-0 fs-14">
                            {i18n.language === "ar"
                              ? item.name.ar
                              : item.name.en}
                          </h5>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="d-flex gap-1 align-items-center my-n2">
                            <UncontrolledDropdown
                              direction="start"
                              className="col text-end"
                            >
                              <DropdownToggle
                                tag="button"
                                className="btn btn-link text-muted p-1 mt-n2 py-0 text-decoration-none fs-15"
                              >
                                <i className="ri-more-fill fs-17 dropdown-menu-button-custom"></i>
                              </DropdownToggle>
                              <DropdownMenu className="dropdown-menu-end">
                                <DropdownItem
                                  style={{
                                    alignSelf: "flex-start",
                                    display: "flex",
                                  }}
                                  onClick={() => onEdit(item.id)}
                                >
                                  <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                                  {t("edit")}
                                </DropdownItem>
                                <DropdownItem
                                  style={{
                                    alignSelf: "flex-start",
                                    display: "flex",
                                  }}
                                  onClick={() => onDelete(item.id)}
                                >
                                  <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                                  {t("delete")}
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </div>
                        </div>
                      </div>
                    </div>
                    <Row>
                      <Col>
                        <div
                          style={{ maxHeight: "40px" }}
                          dangerouslySetInnerHTML={{
                            __html:
                              i18n.language === "ar"
                                ? item.content.ar
                                : item.content.en,
                          }}
                        ></div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            ),
          )}
      </Masonry>
      {!isLoadingSupplications &&
        dataSupplications &&
        dataSupplications.data &&
        dataSupplications.data.length > 0 && (
          <Paginator
            itemsOnCurrentPage={dataSupplications?.data?.length || 0}
            itemsPerPage={dataSupplications?.meta?.itemsPerPage as number}
            totalItems={dataSupplications?.meta?.totalItems || 0}
            currentPage={dataSupplications?.meta?.currentPage || 1}
            totalPages={dataSupplications?.meta?.totalPages || 0}
            onChangePage={(pageToChange: number) => onChangePage(pageToChange)}
          />
        )}
      <DeleteModal
        loading={isLoadingDeleteSupplication}
        show={showDeleteModal}
        onDeleteClick={() => onDeleteConfirm()}
        onCloseClick={() => {
          setShowDeleteModal(false);
        }}
        // TODO: handle error
        // error={errorDeleteSupplication}
      />
      <AddEditSupplicationModal
        id={editingItemId}
        show={showAddEditModal}
        onCloseClick={() => {
          setShowAddEditModal(false);
          getSupplications(paginateQuery);
          setEditingItemId(null);
        }}
      />
    </React.Fragment>
  );
};
